import { Component, EventEmitter, Input, OnChanges,
  OnDestroy, OnInit, Output, SimpleChange } from '@angular/core';
import { TableColumn, TableColumnColorCondition, TableColumnFilter } from '../../../models/app.model';
import { PageService } from '../../../service/page.service';
import { Router } from '@angular/router';
import { AppAlerts } from '../../configs/app.alerts';
import swal from 'sweetalert2';
import { HelperSystem } from '../../helpers/helper.system';
import { RestService } from '../../../service/rest.service';
import * as messageActions from '../../../store/actions/messages';
import { AppMessages } from '../../configs/app.messages';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '../../../store/reducers';
import * as complaintActions from '../../../store/actions/complaint';
import { getSelectedPage } from '../../../store/reducers/complaint';

interface SortData {
  key: string;
  order: string;
}

const COMPLAINT_GRID = 'reception/complaints';

@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss']
})
export class DataGridComponent implements OnInit, OnChanges, OnDestroy {

  @Input() isActionColumn = true;
  @Input() defaultRoute = true;
  @Input() isIdColumn = false;
  @Input() columns: Array<TableColumn> = [];
  @Input() data: Array<any>;
  @Input() isSort = true;
  @Input() isFilter = true;
  @Input() isPaginator = true;
  @Input() isTableHeaderAlignCenter = false;
  @Input() isTotal = false;
  @Input() canDeleteData = true;
  @Input() canEditData = true;
  @Input() canViewData = false;
  // to use with pagination page by page
  @Input() isPageable = false;
  //
  @Input() dataLength = 0;
  //
  @Input() restRemoveUrl: string;
  @Input() showAddButton = true;
  @Input() idGrid: string = 'default'; // for filter
  @Input() isActionExternal: boolean = false;
  @Output() removeId = new EventEmitter<string>();
  @Output() editItem = new EventEmitter<string>();
  // to use with pagination page by page
  @Output() currentPage = new EventEmitter<number>();

  total = 0;

  // dataCopy
  private copyData: Array<any>;
  private subscribers = [];
  filterConditions: Array<TableColumnFilter> = [];

  // pager object
  pager: any = {};
  // paged items
  pagedItems: any[];
  //
  routeLink: string;
  // sorting
  sortKey = null;
  sortOrder = 'asc';
  //
  trColor: Array<String> = [];
  //
  currentPageIndex = 1;
  currentStorePageIndex = 1;

  noDataMessage = 'Таблица не содержит данных, возможно они загружаются...';

  constructor(private store: Store<fromRoot.AppState>,
              private pageService: PageService,
              private router: Router,
              private restService: RestService) {
    this.subscribers.push(
      this.store
        .pipe(
          select(fromRoot.selectComplaintState())
        )
        .subscribe((state: any) => {
          this.currentStorePageIndex = getSelectedPage(state);
        })
    );
  }

  ngOnInit() {
    this.initData();
    if (this.defaultRoute) {
      this.routeLink = this.router.url;
    }
  }

  ngOnDestroy() {
    this.saveSortConditionToCache();
    this.subscribers.forEach( s => s.unsubscribe());
  }

  /**
   * It's needed to catch 'data' property from app store
   */
  ngOnChanges(changes: {[propKey: string]: SimpleChange}) {

    if (this.isPageable && 'dataLength' in changes && changes.dataLength.previousValue !== changes.dataLength.currentValue) {
      this.currentPageIndex = 1;
    }

    if (this.idGrid === COMPLAINT_GRID) {
      this.currentPageIndex =  this.currentStorePageIndex;
    }

    this.initData();

    this.getSortConditionFromCache();

    if (this.sortKey) {
      this._sort();
    }

  }

  private initData() {

    this.copyData = HelperSystem.deepObjClone(this.data);

    if (Object.keys(this.filterConditions).length) {
      this.filterData();
    }

    this.setPage(this.currentPageIndex);

  }

  private countTotal() {
    this.total = 0;
    if (this.isTotal) {
      // get total column
      const totalColumn = this.columns.find(c => c.isTotal === true);
      // count total
      this.data.map( d => {
        this.total += d[totalColumn.sysNameUser] || d[totalColumn.sysName];
      });
      this.total = HelperSystem.toFixed(this.total);
    }
  }

  private _sort() {
    if (!this.isPageable) {
      // sort data
      this.data.sort(HelperSystem.sortDataByKey(this.sortKey, this.sortOrder));
      // set page
      this.setPage(1);
    }
  }

  getFilterCondition(filter: TableColumnFilter) {

    if (!filter || this.isPageable) {
      return;
    }

    // delete filter
    if (!filter.value && this.filterConditions[filter.column]) {
      delete this.filterConditions[filter.column];
    } else {
      // set filter
      this.filterConditions[filter.column] = filter;
    }
    // check conditions
    if (!Object.keys(this.filterConditions).length) {
      this.data = this.copyData;
    } else {
      this.filterData();
    }
    this.setPage(1);

  }

  filterData() {
    this.data = this.copyData.filter(d => {
      const dKeys = Object.keys(d);
      const fKeys = Object.keys(this.filterConditions);
      const conditions = [0, 0, 0, 0]; // init
      fKeys.forEach( f => {
        if (dKeys.includes(f)) {
          const filter = this.filterConditions[f] as TableColumnFilter;
          if (filter.type === 'string' && d[filter.column] && d[filter.column].toLowerCase().indexOf(filter.value) !== -1) {
            conditions[0]++;
          }
          if (filter.type === 'list' && d[filter.column] && d[filter.column] === filter.value) {
            conditions[1]++;
          }
          if (filter.type === 'boolean' && filter.condition === 'equal' && d[filter.column]) {
            if ( (filter.value && d[filter.column]) || (!filter.value && !d[filter.column]) ) {
              conditions[2]++;
            }
          }
          if (filter.type === 'date' && d[filter.column] && d[filter.column].slice(0, 10) === filter.value ) {
              conditions[3]++;
          }
        }
      });
      return conditions.reduce((a, b) => a + b, 0) === fKeys.length ? true : false;
    });
  }


  setUserPage(page: number) {
    this.currentPageIndex = page;
    this.currentPage.emit(page);
  }

  setPage(page: number) {

    const dataLength = this.isPageable ? this.dataLength : this.data.length;

    if (this.data && dataLength) {
      // get pager object from service
      this.pager = this.pageService.getPager(dataLength, page);
      // get current page of items
      this.pagedItems = this.isPageable ? this.data : this.data.slice(this.pager.startIndex, this.pager.endIndex + 1);
      if (this.idGrid === COMPLAINT_GRID) {
        this.store.dispatch(new complaintActions.SetSelectedPage(page));
      }
      this.countTotal();
    } else {
      this.pagedItems = null;
    }

  }

  sort(key: string) {
    this.sortKey = key;
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    this._sort();
  }

  remove(id: string) {
    const _this = this; // reference
    swal({
      title: AppAlerts.DELETE_OBJECT_TITLE,
      text: AppAlerts.DELETE_OBJECT_TEXT,
      type: 'warning',
      showCancelButton: true,
    }).then(function (action) {
      if (action.value === true) {
        _this.restService.removeData(_this.restRemoveUrl, id)
          .subscribe(
            () => {
              _this.store.dispatch(new messageActions.SuccessMessage(AppMessages.APP_REMOVE_SUCCESS));
              _this.data = _this.data.filter( d => d.id !== id);
              _this.initData();
            },
            error => {
              _this.store.dispatch(new messageActions.ErrorMessage(AppMessages.APP_REMOVE_ERROR));
            }
          );
      }
    });
  }

  getTableColumnColor(tdCC: TableColumnColorCondition, value: any, trIndex: number, tdIndex: number) {

    let color = null; // 'inherit';
    // tdIndex === 1 cause we usually don't show id column
    // on the every output of a bunch of rows we should clear the color array
    this.trColor = !trIndex && tdIndex === 1 ? [] : this.trColor;

    if (!tdCC) {
      return;
    }

    switch (tdCC.condition) {
       case 'more':
         color = value > 0 ? tdCC.color : null;
         break;
       case 'equal':
         color = value === tdCC.conditionValue ? tdCC.color : null;
         break;
    }

    this.trColor[trIndex] = color ? color : this.trColor[trIndex];
    return color;

  }

  saveSortConditionToCache() {
    const _sort: SortData = <SortData>{};
    _sort.key = this.sortKey;
    _sort.order = this.sortOrder;
    localStorage.setItem('sort-' + this.idGrid, JSON.stringify(_sort));
  }

  getSortConditionFromCache() {
    const _sort =  JSON.parse(localStorage.getItem('sort-' + this.idGrid)) as SortData;
    if (!_sort) {
      return;
    }
    this.sortOrder = _sort.order || 'desc';
    this.sortKey = _sort.key || null;
  }

  /*clearSortConditionCache() {
    localStorage.removeItem('sort-' + this.idGrid);
  }*/

  removeEmit(id: string) {
    this.removeId.emit(id);
  }

  editEmit(item: any) {
    this.editItem.emit(item);
  }
}
